/**
 * Samsara adapter — partial integration, IOT-DEPLOY-DARK-1 Day 2.
 *
 * Integration scope: ELD telemetry only (GPS, odometer, speed, engine RPM,
 * fuel level, battery). Config push, OTA, and health checks are not
 * implemented — those methods throw VendorNotIntegratedError.
 *
 * Supported models: VG34, VG54, AG26, AG46 (Samsara Vehicle Gateways).
 * Alert surface: cloud-only via Samsara webhook subscription.
 *
 * Samsara API key is injected at factory creation time (constructor opts).
 * This adapter does NOT import fetch or network code — real API calls land
 * in IOT-CLOUD-1. Today this adapter stores and returns normalized readings
 * fed in by the Cloud Function ingest path.
 */

import { NotImplementedAdapter } from '../NotImplementedAdapter.js';
import { VendorNotIntegratedError } from '../VendorNotIntegratedError.js';

const SAMSARA_ELD_CAPABILITIES = {
  configKeys: {},
  telemetryTypes: [
    'gps_position', 'speed_kph', 'odometer_km', 'engine_rpm',
    'fuel_level_pct', 'battery_voltage_v',
  ],
  alertingMode: 'cloud',
  otaSupported: false,
  attestationSupported: false,
  rollbackSupported: false,
  pushLatencyExpectedMs: 0,
};

export class SamsaraAdapter extends NotImplementedAdapter {
  constructor(device) {
    super(device, 'samsara', SAMSARA_ELD_CAPABILITIES);
    /** @type {import('../../contracts/DeviceContract.ts').NormalizedReading[]} */
    this._readings = [];
    /** @type {Array<import('../../contracts/DeviceContract.ts').TelemetryHandler>} */
    this._telemetrySubscribers = [];
  }

  // ── ELD telemetry — partial implementation ──────────────────────────────────

  /**
   * Feed a normalized reading into the adapter (called by iotIngest CF).
   * Notifies all live subscribers.
   *
   * @param {import('../../contracts/DeviceContract.ts').NormalizedReading} reading
   */
  ingestReading(reading) {
    this._readings.push(reading);
    for (const handler of this._telemetrySubscribers) {
      try { handler(reading); } catch { /* subscriber errors must not crash ingest */ }
    }
  }

  /**
   * Return ELD readings for the requested time window and sensor types.
   *
   * @param {import('../../contracts/DeviceContract.ts').TimeWindow} window
   * @param {string[]} [sensorTypes]
   */
  async getTelemetry(window, sensorTypes) {
    let readings = this._readings;
    if (window) {
      const from = new Date(window.from).getTime();
      const to   = new Date(window.to).getTime();
      readings = readings.filter((r) => {
        const ts = new Date(r.timestamp).getTime();
        return ts >= from && ts <= to;
      });
    }
    if (sensorTypes?.length) {
      readings = readings.filter((r) => sensorTypes.includes(r.sensorType));
    }
    return readings;
  }

  subscribeToTelemetry(handler) {
    this._telemetrySubscribers.push(handler);
    return {
      unsubscribe: () => {
        this._telemetrySubscribers = this._telemetrySubscribers.filter((h) => h !== handler);
      },
    };
  }

  // Non-ELD methods remain unimplemented — explicit error, not silent swallow
  async pushConfig()       { throw new VendorNotIntegratedError('samsara', 'pushConfig'); }
  async getCurrentConfig() { throw new VendorNotIntegratedError('samsara', 'getCurrentConfig'); }
  async getPendingConfig() { throw new VendorNotIntegratedError('samsara', 'getPendingConfig'); }
  async rollbackConfig()   { throw new VendorNotIntegratedError('samsara', 'rollbackConfig'); }
  async getDeviceHealth()  { throw new VendorNotIntegratedError('samsara', 'getDeviceHealth'); }
  subscribeToEvents()      { throw new VendorNotIntegratedError('samsara', 'subscribeToEvents'); }
}

/**
 * Factory for HalService.registerAdapter('samsara', createSamsaraAdapterFactory()).
 */
export function createSamsaraAdapterFactory() {
  return (device) => new SamsaraAdapter(device);
}
